// Pure gating predicate for the Ads Express "Generate" button.
//
// Extracted from `ads-express.tsx` (a ~2900-line page that is impractical to
// mount whole in a hermetic DOM test) so the exact invariant that gates ad
// generation can be unit-tested directly. The page imports and uses this
// function, so a regression in the gating rule is caught by the test.
export interface AdsExpressGateInput {
  /** Selected persona object (null when none chosen). */
  persona: unknown | null;
  /** True when promoting an Offer; false when promoting a Product. */
  isOfferMode: boolean;
  /** Selected offer object (only relevant in offer mode). */
  offer: unknown | null;
  /** Selected product object (only relevant in product mode). */
  product: unknown | null;
  /** "custom" means a free-form concept; anything else uses a picked angle. */
  angleMode: string;
  /** Free-form concept text (only relevant in custom angle mode). */
  concept: string;
  /** Selected angle object (only relevant in non-custom angle mode). */
  angle: unknown | null;
  /** Number of ad-unit templates the user has selected. */
  selectedTemplateCount: number;
}

export function canGenerateAds(input: AdsExpressGateInput): boolean {
  const angleReady =
    input.angleMode === "custom" ? !!input.concept.trim() : !!input.angle;
  return (
    !!input.persona &&
    (input.isOfferMode ? !!input.offer : !!input.product) &&
    angleReady &&
    input.selectedTemplateCount > 0
  );
}

export interface AdsExpressBrowsableTemplate {
  isApprovedForBrand: boolean;
}

/**
 * Approval curates the initial browse view only. The caller must retain its
 * original accessible catalog for display lookup, readiness, and generation.
 */
export function getAdsExpressBrowsePool<T extends AdsExpressBrowsableTemplate>(
  accessibleTemplates: T[],
  showAllTemplates: boolean,
): T[] {
  return showAllTemplates
    ? accessibleTemplates
    : accessibleTemplates.filter((template) => template.isApprovedForBrand);
}

export function countHiddenAdsExpressTemplates(
  accessibleTemplates: AdsExpressBrowsableTemplate[],
): number {
  return accessibleTemplates.reduce(
    (count, template) => count + (template.isApprovedForBrand ? 0 : 1),
    0,
  );
}

export function getHistoricalAdsExpressAngleDisplay({
  preservingHistoricalAttachment, angleId, angles, angleNameSnapshot,
}: {
  preservingHistoricalAttachment: boolean;
  angleId: string;
  angles: readonly { id: string; name: string; summary?: string | null }[];
  angleNameSnapshot?: string | null;
}) {
  if (!preservingHistoricalAttachment || !angleId) return undefined;
  const historicalAngle = angles.find((angle) => angle.id === angleId);
  return {
    value: angleId,
    label: historicalAngle?.name || angleNameSnapshot || `Previous narrative angle (${angleId})`,
    description: historicalAngle?.summary ?? null,
  };
}
